import { useCallback, useEffect, useRef, useState } from 'react';

import { getCallScribeTimeEntryOptions } from 'services/callScribe';
import { extractApiErrorMessage } from 'services/callScribe/apiError';
import { ICallScribeTimeEntryOption, ICallScribeTimeEntryOptions } from 'services/callScribe/types';
import {
  defaultOptionLabel,
  pickDefaultOptionValue,
  selectableOptions,
} from 'components/CallScribe/timeEntryOptions';

export interface IUseTimeEntryOptionsResult {
  options: ICallScribeTimeEntryOptions | undefined;
  loading: boolean;
  error: string;
  reload: () => void;
  workTypeChoices: ICallScribeTimeEntryOption[];
  billingChoices: ICallScribeTimeEntryOption[];
  billingDefaultLabel: string | undefined;
  workType: string | undefined;
  billingType: string | undefined;
  setWorkType: (value: string) => void;
  setBillingType: (value: string) => void;
  workTypeHelper: string;
  billingHelper: string;
}

export const helperTextFor = (
  kind: 'work types' | 'billing types',
  options: ICallScribeTimeEntryOptions | undefined,
): string => {
  if (!options) {
    return 'This call has no PSA connection, so the entry uses your PSA default.';
  }
  const workTypes = kind === 'work types';
  const supported = workTypes
    ? options.workTypesSupported ?? options.supported
    : options.billingTypesSupported ?? options.supported;
  const reason = workTypes
    ? options.workTypesUnsupportedReason
    : options.billingTypesUnsupportedReason ?? options.unsupportedReason;
  if (!supported) {
    return reason ?? `This PSA does not expose ${kind}, so the entry uses its default.`;
  }
  const choices = selectableOptions(workTypes ? options.workTypes : options.billingTypes);
  if (choices.length === 0) {
    return `This PSA reports no ${kind} for your tenant.`;
  }
  return workTypes
    ? 'Sourced from your PSA.'
    : 'How the entry is charged on the ticket, as your PSA defines it.';
};

export const useTimeEntryOptions = (
  crmSourceId: number | null | undefined,
  callId?: number,
): IUseTimeEntryOptionsResult => {
  const [options, setOptions] = useState<ICallScribeTimeEntryOptions | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [workType, setWorkType] = useState<string | undefined>(undefined);
  const [billingType, setBillingType] = useState<string | undefined>(undefined);

  const requestGeneration = useRef(0);

  const reload = useCallback(() => {
    if (typeof crmSourceId !== 'number' || crmSourceId <= 0) return;
    requestGeneration.current += 1;
    const generation = requestGeneration.current;
    setLoading(true);
    setError('');
    void (async () => {
      try {
        const res = await getCallScribeTimeEntryOptions(crmSourceId, callId);
        if (requestGeneration.current !== generation) return;
        setOptions(res.data);
      } catch (err) {
        if (requestGeneration.current !== generation) return;
        setError(extractApiErrorMessage(err, 'The work types could not be loaded from your PSA.'));
      } finally {
        if (requestGeneration.current === generation) {
          setLoading(false);
        }
      }
    })();
  }, [crmSourceId, callId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const workTypeChoices = selectableOptions(options?.workTypes);
  const billingChoices = selectableOptions(options?.billingTypes);

  useEffect(() => {
    if (workType === undefined && workTypeChoices.length > 0) {
      setWorkType(pickDefaultOptionValue(options?.workTypes));
    }
  }, [workType, workTypeChoices.length, options]);

  useEffect(() => {
    if (billingType === undefined && billingChoices.length > 0) {
      setBillingType(pickDefaultOptionValue(options?.billingTypes));
    }
  }, [billingType, billingChoices.length, options]);

  return {
    options,
    loading,
    error,
    reload,
    workTypeChoices,
    billingChoices,
    billingDefaultLabel: defaultOptionLabel(options?.billingTypes),
    workType,
    billingType,
    setWorkType,
    setBillingType,
    workTypeHelper: loading
      ? 'Loading your PSA’s work types.'
      : error
        ? 'Your PSA’s work types could not be loaded.'
        : helperTextFor('work types', options),
    billingHelper: loading
      ? 'Loading your PSA’s billing types.'
      : error
        ? 'Your PSA’s billing types could not be loaded.'
        : helperTextFor('billing types', options),
  };
};
